import React, { useCallback, useEffect, useMemo, useState, useRef, createElement } from 'react';
import { Search, Shield, Send, Paperclip, MessageSquare, Plus, X, File, User } from 'lucide-react';
import AuroraFloatingCard from '../ui/AuroraFloatingCard';
// --- TYPES AND MOCK DATA ---
type UserRole = 'Öğrenci' | 'Veli' | 'Öğretmen';
interface User {
  id: number;
  name: string;
  role: UserRole;
  avatar: string;
}
interface Message {
  id: number;
  senderId: number; // 0 for admin
  text: string;
  timestamp: string;
  attachment?: {
    name: string;
    type: string;
    url: string;
    isImage: boolean;
  };
}
interface Conversation {
  id: number;
  userId: number;
  messages: Message[];
  unread: boolean;
}
const mockUsers: User[] = [{
  id: 1,
  name: 'Ayşe Yılmaz',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/1.jpg'
}, {
  id: 2,
  name: 'Fatma Kaya',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/2.jpg'
}, {
  id: 3,
  name: 'Mehmet Öztürk',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/3.jpg'
}, {
  id: 4,
  name: 'Zeynep Çelik',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/4.jpg'
}, {
  id: 5,
  name: 'Mustafa Arslan',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/5.jpg'
},
// Expanded user data for better search testing
{
  id: 6,
  name: 'Ali Demir',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/men/6.jpg'
}, {
  id: 7,
  name: 'Aylin Şahin',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/7.jpg'
}, {
  id: 8,
  name: 'Burak Yıldız',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/men/8.jpg'
}, {
  id: 9,
  name: 'Canan Aksoy',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/9.jpg'
}, {
  id: 10,
  name: 'Deniz Kılıç',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/10.jpg'
}, {
  id: 11,
  name: 'Efe Çetin',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/men/11.jpg'
}, {
  id: 12,
  name: 'Elif Yıldırım',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/12.jpg'
}, {
  id: 13,
  name: 'Ferhat Koç',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/13.jpg'
}, {
  id: 14,
  name: 'Gizem Aydın',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/14.jpg'
}, {
  id: 15,
  name: 'Hakan Korkmaz',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/15.jpg'
}, {
  id: 16,
  name: 'İrem Yılmaz',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/16.jpg'
}, {
  id: 17,
  name: 'Kemal Özdemir',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/men/17.jpg'
}, {
  id: 18,
  name: 'Leyla Çelik',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/18.jpg'
}, {
  id: 19,
  name: 'Murat Kaya',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/19.jpg'
}, {
  id: 20,
  name: 'Nalan Demir',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/20.jpg'
}, {
  id: 21,
  name: 'Okan Şahin',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/21.jpg'
}, {
  id: 22,
  name: 'Pınar Yıldız',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/22.jpg'
}, {
  id: 23,
  name: 'Rıza Aksoy',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/men/23.jpg'
}, {
  id: 24,
  name: 'Sevgi Kılıç',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/women/24.jpg'
}, {
  id: 25,
  name: 'Tolga Çetin',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/men/25.jpg'
}, {
  id: 26,
  name: 'Ümit Yıldırım',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/men/26.jpg'
}, {
  id: 27,
  name: 'Vildan Koç',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/women/27.jpg'
}, {
  id: 28,
  name: 'Yusuf Aydın',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/men/28.jpg'
}, {
  id: 29,
  name: 'Zehra Korkmaz',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/29.jpg'
}, {
  id: 30,
  name: 'Ahmet Yılmaz',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/men/30.jpg'
}, {
  id: 31,
  name: 'Berna Özdemir',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/women/31.jpg'
}, {
  id: 32,
  name: 'Cenk Demir',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
}, {
  id: 33,
  name: 'Derya Şahin',
  role: 'Öğretmen',
  avatar: 'https://randomuser.me/api/portraits/women/33.jpg'
}, {
  id: 34,
  name: 'Emre Yıldız',
  role: 'Öğrenci',
  avatar: 'https://randomuser.me/api/portraits/men/34.jpg'
}, {
  id: 35,
  name: 'Fulya Aksoy',
  role: 'Veli',
  avatar: 'https://randomuser.me/api/portraits/women/35.jpg'
}];
const mockConversations: Conversation[] = [{
  id: 1,
  userId: 1,
  messages: [{
    id: 1,
    senderId: 1,
    text: 'Merhaba, matematik ödevim hakkında soru sormak istiyorum.',
    timestamp: '14:30'
  }, {
    id: 2,
    senderId: 0,
    text: 'Merhaba Ayşe, tabii ki. Hangi konuda yardıma ihtiyacın var?',
    timestamp: '14:32'
  }],
  unread: true
}, {
  id: 2,
  userId: 2,
  messages: [{
    id: 1,
    senderId: 2,
    text: 'Çocuğumun devamsızlık durumu hakkında bilgi alabilir miyim?',
    timestamp: '10:15'
  }, {
    id: 2,
    senderId: 0,
    text: 'Merhaba, tabii ki. Çocuğunuzun devamsızlık raporunu hazırlayıp size göndereceğim.',
    timestamp: '10:18'
  }],
  unread: false
}, {
  id: 3,
  userId: 3,
  messages: [{
    id: 1,
    senderId: 3,
    text: 'Yeni müfredat değişiklikleri hakkında toplantı yapalım mı?',
    timestamp: '16:45'
  }],
  unread: true
}];
// --- MAIN COMPONENT ---
export function YoneticiMesajlar() {
  const [activeTab, setActiveTab] = useState<UserRole>('Öğrenci');
  const [searchTerm, setSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<number>(100); // Default to support
  // State for new conversation form
  const [newConversationData, setNewConversationData] = useState({
    userId: '',
    message: ''
  });
  // State for conversations (initialize with mock data)
  const [conversations, setConversations] = useState<Conversation[]>([{
    id: 100,
    userId: 0,
    messages: [{
      id: 1,
      senderId: 999,
      text: 'Merhaba, Eğitim Pilotu Destek ekibine hoş geldiniz. Size nasıl yardımcı olabiliriz?',
      timestamp: '09:30'
    }, {
      id: 2,
      senderId: 0,
      text: 'Merhaba, öğretmen panelinde bir sorun yaşıyorum.',
      timestamp: '09:35'
    }, {
      id: 3,
      senderId: 999,
      text: 'Lütfen yaşadığınız sorunu detaylandırır mısınız?',
      timestamp: '09:37'
    }],
    unread: false
  }, ...mockConversations]);
  // Filter conversations based on search term only (removed tab filtering)
  const filteredConversations = useMemo(() => {
    return conversations.filter(convo => {
      if (convo.id === 100) return false; // Exclude support from filtered list
      const user = mockUsers.find(u => u.id === convo.userId);
      if (!user) return false;
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    }).map(convo => ({
      ...convo,
      user: mockUsers.find(u => u.id === convo.userId)
    }));
  }, [searchTerm, conversations]);
  // Get the selected conversation
  const selectedConversation = useMemo(() => {
    if (selectedConversationId === 100) {
      return {
        ...conversations.find(c => c.id === 100),
        user: {
          id: 0,
          name: 'Eğitim Pilotu Destek',
          role: 'Öğretmen' as UserRole,
          avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/c7KMvBc4x3hGaD7STUj5xt/Image_fx_%2811%29.jpg'
        }
      };
    }
    const convo = conversations.find(c => c.id === selectedConversationId);
    if (!convo) return null;
    return {
      ...convo,
      user: mockUsers.find(u => u.id === convo.userId)
    };
  }, [selectedConversationId, conversations]);
  // Get current timestamp in HH:MM format
  const getCurrentTimestamp = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  };
  // Handle sending a new message
  const handleSendMessage = () => {
    if (!newMessage.trim() && !selectedFile || !selectedConversation) return;
    // Create new message object
    const newMessageObj: Message = {
      id: Date.now(),
      senderId: 0,
      text: newMessage.trim(),
      timestamp: getCurrentTimestamp()
    };
    // Add attachment if file is selected
    if (selectedFile) {
      const isImage = selectedFile.type.startsWith('image/');
      newMessageObj.attachment = {
        name: selectedFile.name,
        type: selectedFile.type,
        url: filePreview || '',
        isImage
      };
    }
    // Update conversation with new message
    setConversations(prevConversations => prevConversations.map(convo => convo.id === selectedConversationId ? {
      ...convo,
      messages: [...convo.messages, newMessageObj]
    } : convo));
    // Clear input and file selection
    setNewMessage('');
    setSelectedFile(null);
    setFilePreview(null);
  };
  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          if (e.target?.result) {
            setFilePreview(e.target.result as string);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };
  // Clear selected file
  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Handle creating a new conversation
  const handleCreateNewConversation = (userId: string, message: string) => {
    if (!userId || !message.trim()) return;
    const parsedUserId = parseInt(userId);
    // Check if conversation already exists
    const existingConversation = conversations.find(c => c.userId === parsedUserId && c.id !== 100);
    if (existingConversation) {
      // Add message to existing conversation
      const updatedConversations = conversations.map(convo => convo.id === existingConversation.id ? {
        ...convo,
        messages: [...convo.messages, {
          id: Date.now(),
          senderId: 0,
          text: message.trim(),
          timestamp: getCurrentTimestamp()
        }]
      } : convo);
      setConversations(updatedConversations);
      setSelectedConversationId(existingConversation.id);
    } else {
      // Create new conversation
      const newConvo: Conversation = {
        id: Math.max(...conversations.map(c => c.id)) + 1,
        userId: parsedUserId,
        messages: [{
          id: Date.now(),
          senderId: 0,
          text: message.trim(),
          timestamp: getCurrentTimestamp()
        }],
        unread: false
      };
      setConversations([...conversations, newConvo]);
      setSelectedConversationId(newConvo.id);
    }
    // Reset form and close modal
    setNewConversationData({
      userId: '',
      message: ''
    });
    setShowNewConversationModal(false);
  };
  // New Conversation Modal component
  const NewMessageModal = () => {
    // Local state for the modal
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [messageText, setMessageText] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    // Refs for input elements
    const searchInputRef = useRef<HTMLInputElement>(null);
    const messageInputRef = useRef<HTMLTextAreaElement>(null);
    // Filter users based on search query
    const filteredUsers = useMemo(() => {
      if (!searchQuery.trim()) return mockUsers;
      return mockUsers.filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.role.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [searchQuery]);
    // Find selected user details
    const selectedUser = useMemo(() => {
      if (!selectedUserId) return null;
      return mockUsers.find(user => user.id.toString() === selectedUserId);
    }, [selectedUserId]);
    // Handle user selection from dropdown
    const handleSelectUser = (userId: number) => {
      setSelectedUserId(userId.toString());
      setShowDropdown(false);
      setSearchQuery('');
      // Focus the message textarea after selecting a user
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 100);
    };
    // Handle clearing the selected user
    const handleClearUser = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent dropdown from opening
      setSelectedUserId('');
      setSearchQuery('');
      // Focus the search input after clearing
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    };
    // Handle sending the message
    const handleSend = () => {
      if (!selectedUserId || !messageText.trim()) return;
      handleCreateNewConversation(selectedUserId, messageText);
    };
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.user-search-container') && showDropdown) {
          setShowDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);
    return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-1538">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowNewConversationModal(false)} data-id="element-1539" />
        {/* Modal Content */}
        <AuroraFloatingCard className="relative z-10 w-full max-w-lg" data-id="element-1540">
          <div className="p-6" data-id="element-1541">
            {/* Header with close button */}
            <div className="flex items-center justify-between mb-6" data-id="element-1542">
              <h3 className="text-xl font-bold text-white" data-id="element-1543">
                Yeni Mesaj Başlat
              </h3>
              <button onClick={() => setShowNewConversationModal(false)} className="p-1.5 rounded-full hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors" data-id="element-1544">
                <X size={20} data-id="element-1545" />
              </button>
            </div>
            {/* User search field */}
            <div className="mb-5" data-id="element-1546">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1547">
                Kime:
              </label>
              <div className="user-search-container relative" data-id="element-1548">
                <div className={`w-full bg-gray-800/50 backdrop-blur-sm border border-purple-500/30 rounded-lg px-3 py-2.5 cursor-text
                    ${showDropdown ? 'ring-2 ring-purple-500 border-transparent' : 'hover:border-purple-500/50'}`} onClick={() => {
                setShowDropdown(true);
                if (!selectedUserId && searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }} data-id="element-1549">
                  {selectedUser ? <div className="flex items-center justify-between" data-id="element-1550">
                      <div className="flex items-center gap-2" data-id="element-1551">
                        <img src={selectedUser.avatar} alt={selectedUser.name} className="w-7 h-7 rounded-full object-cover" data-id="element-1552" />
                        <span className="text-white" data-id="element-1553">
                          {selectedUser.name}{' '}
                          <span className="text-gray-400 text-sm" data-id="element-1554">
                            ({selectedUser.role})
                          </span>
                        </span>
                      </div>
                      <button className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10" onClick={handleClearUser} data-id="element-1555">
                        <X size={16} data-id="element-1556" />
                      </button>
                    </div> : <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Bir kişi arayın veya seçin..." className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400" onFocus={() => setShowDropdown(true)} data-id="element-1557" />}
                </div>
                {/* Dropdown for user selection */}
                {showDropdown && <div className="absolute z-20 w-full mt-1 py-1 bg-gray-800/95 backdrop-blur-md border border-purple-500/30 rounded-lg shadow-xl max-h-60 overflow-y-auto custom-scrollbar" data-id="element-1558">
                    {filteredUsers.length > 0 ? filteredUsers.map(user => <div key={user.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer text-gray-200 hover:bg-purple-600/20 transition-colors" onClick={() => handleSelectUser(user.id)} data-id="element-1559">
                          <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" data-id="element-1560" />
                          <div data-id="element-1561">
                            <div className="text-white font-medium" data-id="element-1562">
                              {user.name}
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-1563">
                              {user.role}
                            </div>
                          </div>
                        </div>) : <div className="px-3 py-4 text-center text-gray-400" data-id="element-1564">
                        Sonuç bulunamadı
                      </div>}
                  </div>}
              </div>
            </div>
            {/* Message textarea */}
            <div className="mb-6" data-id="element-1565">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1566">
                Mesajınız:
              </label>
              <textarea ref={messageInputRef} value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Mesajınızı yazın..." className="w-full h-32 bg-gray-800/50 border border-purple-500/30 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400 custom-scrollbar" data-id="element-1567" />
            </div>
            {/* Action buttons */}
            <div className="flex justify-end" data-id="element-1568">
              <button onClick={handleSend} disabled={!selectedUserId || !messageText.trim()} className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1569">
                Gönder
              </button>
            </div>
          </div>
        </AuroraFloatingCard>
      </div>;
  };
  // Render file preview component
  const FilePreview = () => {
    if (!selectedFile) return null;
    return <div className="p-2 bg-gray-700/50 rounded-lg mb-2 flex items-center gap-2" data-id="element-1570">
        {filePreview && selectedFile.type.startsWith('image/') ? <div className="relative w-12 h-12" data-id="element-1571">
            <img src={filePreview} alt="Preview" className="w-12 h-12 object-cover rounded" data-id="element-1572" />
          </div> : <div className="bg-gray-600 w-12 h-12 rounded flex items-center justify-center" data-id="element-1573">
            <File size={20} className="text-gray-300" data-id="element-1574" />
          </div>}
        <div className="flex-1 overflow-hidden" data-id="element-1575">
          <p className="text-sm text-white truncate" data-id="element-1576">{selectedFile.name}</p>
          <p className="text-xs text-gray-400" data-id="element-1577">
            {(selectedFile.size / 1024).toFixed(0)} KB
          </p>
        </div>
        <button onClick={clearSelectedFile} className="p-1 hover:bg-gray-600 rounded" data-id="element-1578">
          <X size={16} className="text-gray-300" data-id="element-1579" />
        </button>
      </div>;
  };
  // Render message attachment
  const MessageAttachment = ({
    attachment
  }) => {
    if (!attachment) return null;
    return <div className="mt-2" data-id="element-1580">
        {attachment.isImage ? <div className="relative" data-id="element-1581">
            <img src={attachment.url} alt={attachment.name} className="max-w-full rounded max-h-48 object-contain" data-id="element-1582" />
          </div> : <div className="flex items-center gap-2 p-2 bg-gray-700/50 rounded" data-id="element-1583">
            <File size={18} className="text-gray-300" data-id="element-1584" />
            <span className="text-sm text-white" data-id="element-1585">{attachment.name}</span>
          </div>}
      </div>;
  };
  return <div className="space-y-6" data-id="element-1586">
      {/* New Conversation Modal */}
      {showNewConversationModal && <NewMessageModal data-id="element-1587" />}
      <div className="flex items-center gap-3" data-id="element-1588">
        <h1 className="text-3xl font-bold" data-id="element-1589">Mesajlarım</h1>
        <MessageSquare className="w-8 h-8 text-blue-400" data-id="element-1590" />
      </div>
      <AuroraFloatingCard className="h-[calc(100vh-12rem)]" data-id="element-1591">
        <div className="flex h-full flex-col md:flex-row" data-id="element-1592">
          {/* Left Column */}
          <div className="w-full md:w-[320px] lg:w-[360px] border-b md:border-b-0 md:border-r border-purple-500/20 flex flex-col" data-id="element-1593">
            <div className="p-4 md:p-5 space-y-4" data-id="element-1594">
              {/* Search Bar with New Conversation Button */}
              <div className="flex gap-3" data-id="element-1595">
                <div className="relative flex-1" data-id="element-1596">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} data-id="element-1597" />
                  <input type="text" placeholder="Kişilerde ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-11 bg-gray-800/50 border border-purple-500/30 rounded-lg pl-10 pr-4 py-2.5 text-sm md:text-base focus:ring-2 focus:ring-purple-400 focus:outline-none" data-id="element-1598" />
                </div>
                <button onClick={() => setShowNewConversationModal(true)} className="h-11 w-11 flex items-center justify-center bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-400 transition-colors flex-shrink-0" title="Yeni mesaj başlat" data-id="element-1599">
                  <Plus size={20} data-id="element-1600" />
                </button>
              </div>
              {/* Support Card */}
              <div className={`${selectedConversationId === 100 ? 'bg-blue-500/20' : 'bg-blue-500/10 hover:bg-blue-500/20'} p-4 rounded-lg flex items-center space-x-3 cursor-pointer transition-colors`} onClick={() => setSelectedConversationId(100)} data-id="element-1601">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-1602">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/c7KMvBc4x3hGaD7STUj5xt/Image_fx_%2811%29.jpg" alt="Eğitim Pilotu Destek" className="w-full h-full object-cover" data-id="element-1603" />
                </div>
                <span className="font-semibold text-white truncate" data-id="element-1604">
                  Eğitim Pilotu Destek
                </span>
              </div>
            </div>
            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar" data-id="element-1605">
              <h3 className="px-2 py-3 text-sm font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1606">
                Tüm Mesajlar
              </h3>
              <ul data-id="element-1607">
                {filteredConversations.map(convo => <li key={convo.id} onClick={() => setSelectedConversationId(convo.id)} className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors space-x-3 ${selectedConversationId === convo.id ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20' : 'hover:bg-gray-700/50'}`} data-id="element-1608">
                    <img src={convo.user?.avatar} alt={convo.user?.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1609" />
                    <div className="flex-1 overflow-hidden" data-id="element-1610">
                      <div className="flex justify-between items-center" data-id="element-1611">
                        <p className={`${convo.unread ? 'font-bold' : 'font-semibold'} text-white truncate`} data-id="element-1612">
                          {convo.user?.name}
                        </p>
                        <p className="text-xs text-gray-500 flex-shrink-0 ml-1" data-id="element-1613">
                          {convo.messages[convo.messages.length - 1].timestamp}
                        </p>
                      </div>
                      <div className="flex justify-between items-start" data-id="element-1614">
                        <p className="text-sm text-gray-400 truncate" data-id="element-1615">
                          {convo.messages[convo.messages.length - 1].text || (convo.messages[convo.messages.length - 1].attachment ? convo.messages[convo.messages.length - 1].attachment.isImage ? '🖼️ Görsel' : '📎 Dosya' : '')}
                        </p>
                        {convo.unread && <div className="w-2.5 h-2.5 bg-blue-400 rounded-full flex-shrink-0 mt-1 ml-1" data-id="element-1616"></div>}
                      </div>
                    </div>
                  </li>)}
                {filteredConversations.length === 0 && <li className="p-4 text-center text-gray-500" data-id="element-1617">
                    Mesaj bulunamadı. Yeni bir mesaj başlatabilirsiniz.
                  </li>}
              </ul>
            </div>
          </div>
          {/* Right Column */}
          <div className="flex-1 flex flex-col h-full md:h-auto" data-id="element-1618">
            {selectedConversation ? <>
                {/* Conversation Header */}
                <div className="flex items-center p-4 border-b border-purple-500/20" data-id="element-1619">
                  <img src={selectedConversation.user?.avatar} alt={selectedConversation.user?.name} className="w-12 h-12 rounded-full object-cover mr-4 flex-shrink-0" data-id="element-1620" />
                  <div className="overflow-hidden" data-id="element-1621">
                    <p className="font-semibold text-lg text-white truncate" data-id="element-1622">
                      {selectedConversation.user?.name}
                    </p>
                    <p className="text-sm text-gray-400 truncate" data-id="element-1623">
                      {selectedConversation.id === 100 ? 'Teknik Destek' : selectedConversation.user?.role}
                    </p>
                  </div>
                </div>
                {/* Messages Area */}
                <div className="flex-1 p-6 space-y-4 overflow-y-auto custom-scrollbar" data-id="element-1624">
                  {selectedConversation.messages.map(msg => <div key={msg.id} className={`flex ${msg.senderId === 0 ? 'justify-end' : 'justify-start'}`} data-id="element-1625">
                      <div className={`max-w-[75%] sm:max-w-xs lg:max-w-md px-4 py-2 rounded-xl ${msg.senderId === 0 ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white' : 'bg-gray-700 text-gray-200'}`} data-id="element-1626">
                        {msg.text && <p className="break-words" data-id="element-1627">{msg.text}</p>}
                        {/* Render attachment if exists */}
                        {msg.attachment && <MessageAttachment attachment={msg.attachment} data-id="element-1628" />}
                        <p className="text-xs opacity-70 text-right mt-1" data-id="element-1629">
                          {msg.timestamp}
                        </p>
                      </div>
                    </div>)}
                </div>
                {/* File Preview */}
                {selectedFile && <FilePreview data-id="element-1630" />}
                {/* Hidden file input */}
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelect} data-id="element-1631" />
                {/* Message Composer */}
                <div className="p-4 mt-auto border-t border-purple-500/20" data-id="element-1632">
                  <div className="relative" data-id="element-1633">
                    <textarea placeholder="Bir mesaj yazın..." value={newMessage} onChange={e => setNewMessage(e.target.value)} className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg pl-4 pr-20 py-3 resize-none focus:ring-2 focus:ring-purple-400 focus:outline-none custom-scrollbar" rows={2} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }} data-id="element-1634" />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-2" data-id="element-1635">
                      <button className="text-gray-400 hover:text-white p-1" onClick={() => fileInputRef.current?.click()} title="Dosya ekle" data-id="element-1636">
                        <Paperclip size={20} data-id="element-1637" />
                      </button>
                      <button className="p-2 bg-purple-600 rounded-lg text-white hover:bg-purple-700 disabled:opacity-50 disabled:bg-purple-600/50" onClick={handleSendMessage} disabled={!newMessage.trim() && !selectedFile} title="Gönder" data-id="element-1638">
                        <Send size={20} data-id="element-1639" />
                      </button>
                    </div>
                  </div>
                </div>
              </> : <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4" data-id="element-1640">
                <MessageSquare size={64} data-id="element-1641" />
                <p className="mt-4 text-lg text-center" data-id="element-1642">
                  Görüntülemek için bir konuşma seçin
                </p>
              </div>}
          </div>
        </div>
      </AuroraFloatingCard>
    </div>;
}
// Add this CSS to hide scrollbar but allow scrolling
const style = document.createElement('style');
style.textContent = `
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
`;
document.head.appendChild(style);